/**
 * ТО техники по моточасам (ТХ-03): план «единица × месяц», шкала наработки с отметками ТО, регламент компании,
 * отметка о выполненном ТО и заявка на запчасти (уходит в заявки закупки проекта).
 */
import {
  SERVICE_STATE_LABELS,
  type ServiceItem,
  type ServicePlan,
  type ServicePlanUnit,
  type ServiceRuleRow,
  type ServiceState,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { api } from '../../lib/api';
import { todayLocal } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, type Tone, toast } from '../../ui/feedback';
import { Input, Textarea } from '../../ui/form';
import { Panel } from '../../ui/layout';
import { LField } from '../schedule/LField';
import { dd, n1, num, useFleetRights } from './shared';

const TONE: Record<ServiceState, Tone> = {
  overdue: 'danger',
  soon: 'warning',
  ok: 'success',
  unknown: 'neutral',
};
const MONTH = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const monthLabel = (m: string) => `${MONTH[Number(m.slice(5, 7)) - 1]} ${m.slice(2, 4)}`;

/** Самое срочное состояние единицы */
const worst = (u: ServicePlanUnit): ServiceState =>
  (['overdue', 'soon', 'unknown', 'ok'] as const).find((s) => u.items.some((i) => i.state === s)) ??
  'unknown';

export function ServicePanel({ projectId, open }: { projectId: string | undefined; open?: string }) {
  const plan = useQuery({
    queryKey: ['fleet', 'service-plan', projectId ?? ''],
    queryFn: () => api<ServicePlan>('/fleet/service-plan', { query: { projectId } }),
  });
  const [openId, setOpenId] = useState<string | undefined>(open);
  const [rules, setRules] = useState(false);
  const me = useMe();
  const wide = Boolean(me.access?.tenant.includes('resources.edit'));
  const unit = plan.data?.units.find((u) => u.machineId === openId);
  return (
    <Panel
      title="ТО по моточасам"
      meta="прогноз по наработке из путевых листов"
      actions={
        <Button onClick={() => setRules(true)}>{wide ? 'Регламент ТО' : 'Регламент ТО (просмотр)'}</Button>
      }
    >
      {plan.isPending ? <Spinner /> : null}
      {plan.isError ? <ErrorBox error={plan.error} /> : null}
      {plan.data && !plan.data.units.some((u) => u.items.length) ? (
        <EmptyState title="Регламента ТО ещё нет">
          Заведите регламент (например «ТО-250» каждые 250 моточасов) — портал начнёт считать, когда техника
          дойдёт до очередного ТО.
        </EmptyState>
      ) : null}
      {plan.data?.units.length ? <PlanGrid plan={plan.data} onOpen={setOpenId} /> : null}
      {unit ? <UnitDialog unit={unit} onClose={() => setOpenId(undefined)} /> : null}
      {rules ? <RulesDialog canEdit={wide} onClose={() => setRules(false)} /> : null}
    </Panel>
  );
}

function PlanGrid({ plan, onOpen }: { plan: ServicePlan; onOpen: (id: string) => void }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[920px] border-collapse text-[13px]">
        <thead>
          <tr className="text-left text-[12px] text-ink-2">
            <th className="sticky left-0 bg-surface py-2 pr-3">Единица</th>
            <th className="px-1">Мч / в день</th>
            {plan.months.map((m) => (
              <th key={m} className="px-1 text-center font-normal">
                {monthLabel(m)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {plan.units.map((u) => (
            <tr key={u.machineId}>
              <td className="sticky left-0 bg-surface py-2 pr-3">
                <button
                  type="button"
                  className="min-h-11 text-left font-medium underline-offset-2 hover:underline [overflow-wrap:anywhere]"
                  onClick={() => onOpen(u.machineId)}
                >
                  {u.name}
                  {u.regNumber ? ` · ${u.regNumber}` : ''}
                </button>
                <span className="block">
                  <Badge tone={TONE[worst(u)]}>{SERVICE_STATE_LABELS[worst(u)]}</Badge>
                </span>
              </td>
              <td className="px-1 text-ink-2">
                {u.hourMeter == null ? '—' : n1(u.hourMeter)} / {n1(u.perDay)}
              </td>
              {plan.months.map((m, mi) => {
                const due = u.items.filter((i) => i.forecast.some((d) => d.slice(0, 7) === m));
                return (
                  <td key={m} className="px-1 text-center align-top">
                    {due.map((i) => (
                      <span
                        key={i.ruleId}
                        title={`${i.ruleName}: ${i.state === 'overdue' && mi === 0 ? 'просрочено' : 'ожидается'}`}
                        className={`m-0.5 inline-block rounded px-1.5 py-0.5 text-[11px] ${
                          i.state === 'overdue' && mi === 0
                            ? 'bg-danger/15 text-danger'
                            : 'bg-accent/10 text-ink'
                        }`}
                      >
                        {i.ruleName}
                        {i.forecast.filter((d) => d.slice(0, 7) === m).length > 1
                          ? ` ×${i.forecast.filter((d) => d.slice(0, 7) === m).length}`
                          : ''}
                      </span>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Шкала наработки одного регламента: от нуля до следующего ТО, отметки прошлых ТО и текущий счётчик */
function Scale({ item, meter }: { item: ServiceItem; meter: number | null }) {
  if (item.everyHours == null) {
    return (
      <p className="text-[12px] text-ink-2">
        По календарю: каждые {item.everyDays} дн.
        {item.lastDay ? `, последнее ${dd(item.lastDay)}` : ', отметок нет'}
      </p>
    );
  }
  const pts = [
    ...item.marks.map((m) => m.hourMeter).filter((x): x is number => x != null),
    ...(meter != null ? [meter] : []),
    ...(item.nextHours != null ? [item.nextHours] : []),
  ];
  const lo = Math.min(...pts, item.lastHours ?? Infinity);
  const from = Number.isFinite(lo) ? lo : 0;
  const to = Math.max(...pts, from + item.everyHours);
  const span = Math.max(1, to - from);
  const at = (x: number) => `${Math.min(100, Math.max(0, ((x - from) / span) * 100))}%`;
  return (
    <div>
      <div
        className="relative mx-2 my-5 h-2 rounded bg-line"
        role="img"
        aria-label={`Шкала наработки: ${item.ruleName}`}
      >
        {item.marks.map((m) =>
          m.hourMeter == null ? null : (
            <span
              key={`${m.day}${m.hourMeter}`}
              title={`ТО ${dd(m.day)}, ${n1(m.hourMeter)} мч`}
              className="absolute top-[-4px] h-4 w-1 rounded bg-success"
              style={{ left: at(m.hourMeter) }}
            />
          ),
        )}
        {item.nextHours != null ? (
          <span
            title={`Следующее ТО: ${n1(item.nextHours)} мч`}
            className="absolute top-[-6px] h-5 w-1.5 rounded bg-danger"
            style={{ left: at(item.nextHours) }}
          />
        ) : null}
        {meter != null ? (
          <span
            title={`Счётчик: ${n1(meter)} мч`}
            className="absolute top-[-9px] size-3 -translate-x-1/2 rounded-full border-2 border-surface bg-accent"
            style={{ left: at(meter) }}
          />
        ) : null}
      </div>
      <p className="text-[12px] text-ink-2">
        {n1(from)} → {n1(to)} мч · зелёные — прошлые ТО, красная — следующее
        {item.nextHours != null ? ` (${n1(item.nextHours)} мч)` : ''}, синяя точка — счётчик сейчас
      </p>
    </div>
  );
}

function UnitDialog({ unit, onClose }: { unit: ServicePlanUnit; onClose: () => void }) {
  const rights = useFleetRights();
  const canEdit = rights.canEdit(unit.projectId);
  const [mark, setMark] = useState<ServiceItem | null>(null);
  const [parts, setParts] = useState<ServiceItem | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`${unit.name}${unit.regNumber ? ` · ${unit.regNumber}` : ''}`}
      description={`Счётчик ${unit.hourMeter == null ? 'не задан' : `${n1(unit.hourMeter)} мч`}, наработка ${n1(unit.perDay)} мч в день за 30 дней${unit.model ? `, модель ${unit.model}` : ''}`}
      width="lg"
      footer={<Button onClick={onClose}>Закрыть</Button>}
    >
      {!unit.items.length ? <p className="text-[13px] text-ink-2">Регламента для этой модели нет.</p> : null}
      <ul className="flex flex-col gap-4">
        {unit.items.map((i) => (
          <li key={i.ruleId} className="rounded-md border border-line p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-medium">
                {i.ruleName}
                <span className="ml-2 text-[12px] font-normal text-ink-2">
                  {[
                    i.everyHours != null ? `каждые ${n1(i.everyHours)} мч` : null,
                    i.everyDays != null ? `каждые ${i.everyDays} дн.` : null,
                  ]
                    .filter(Boolean)
                    .join(' или ')}
                </span>
              </span>
              <Badge tone={TONE[i.state]}>{SERVICE_STATE_LABELS[i.state]}</Badge>
            </div>
            <p className="mt-1 text-[13px]">
              {i.hoursLeft != null
                ? i.hoursLeft <= 0
                  ? `Перепробег ${n1(-i.hoursLeft)} мч. `
                  : `До ТО ${n1(i.hoursLeft)} мч. `
                : ''}
              {i.nextDay ? `Ожидается ${dd(i.nextDay)}${i.basis === 'days' ? ' (по календарю)' : ''}. ` : ''}
              {i.lastDay || i.lastHours != null
                ? `Последнее ТО: ${i.lastDay ? dd(i.lastDay) : 'дата не указана'}${i.lastHours != null ? `, ${n1(i.lastHours)} мч` : ''}.`
                : 'Отметок о ТО нет — отметьте последнее ТО, чтобы считать прогноз.'}
            </p>
            <Scale item={i} meter={unit.hourMeter} />
            <div className="mt-2 flex flex-wrap items-center gap-2">
              {canEdit ? <Button onClick={() => setMark(i)}>Отметить ТО</Button> : null}
              {i.partsRequest ? (
                <Link
                  to="/procurement/$requestId"
                  params={{ requestId: i.partsRequest.id }}
                  className="inline-flex min-h-11 items-center text-[13px] underline"
                >
                  Заявка на запчасти № {i.partsRequest.number}
                </Link>
              ) : (
                <Button onClick={() => setParts(i)}>Заявка на запчасти</Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      {mark ? <MarkDialog unit={unit} item={mark} onClose={() => setMark(null)} /> : null}
      {parts ? <PartsDialog unit={unit} item={parts} onClose={() => setParts(null)} /> : null}
    </Dialog>
  );
}

function MarkDialog({
  unit,
  item,
  onClose,
}: {
  unit: ServicePlanUnit;
  item: ServiceItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [day, setDay] = useState(todayLocal());
  const [meter, setMeter] = useState(unit.hourMeter == null ? '' : String(unit.hourMeter));
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () =>
      api(`/fleet/units/${unit.machineId}/service-marks`, {
        method: 'POST',
        body: { ruleId: item.ruleId, day, hourMeter: num(meter), note: note.trim() || null },
      }),
    onSuccess: () => {
      toast('ТО отмечено');
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Отметить ТО: ${item.ruleName}`}
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button variant="primary" loading={save.isPending} onClick={() => save.mutate()}>
            Записать
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <LField label="Дата ТО">
          <Input type="date" className="h-11" value={day} onChange={(e) => setDay(e.target.value)} />
        </LField>
        <LField label="Моточасы на счётчике">
          <Input
            inputMode="decimal"
            className="h-11"
            value={meter}
            onChange={(e) => setMeter(e.target.value)}
          />
        </LField>
        <LField label="Заметка">
          <Input className="h-11" value={note} onChange={(e) => setNote(e.target.value)} />
        </LField>
      </div>
    </Dialog>
  );
}

function PartsDialog({
  unit,
  item,
  onClose,
}: {
  unit: ServicePlanUnit;
  item: ServiceItem;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const hint = useQuery({
    queryKey: ['fleet', 'service-rules'],
    queryFn: () => api<ServiceRuleRow[]>('/fleet/service-rules'),
  });
  const rule = hint.data?.find((r) => r.id === item.ruleId);
  const [text, setText] = useState<string | null>(null);
  const body = text ?? rule?.partsHint ?? '';
  const lines = body
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((name) => ({ name, unit: 'шт', quantity: 1 }));
  const [required, setRequired] = useState(item.nextDay ?? '');
  const save = useMutation({
    mutationFn: () =>
      api<{ requestId: string; number: number }>(`/fleet/units/${unit.machineId}/parts-request`, {
        method: 'POST',
        body: { ruleId: item.ruleId, requiredDate: required || null, lines },
      }),
    onSuccess: (r) => {
      toast(`Заявка № ${r.number} создана в закупках`);
      void qc.invalidateQueries({ queryKey: ['fleet'] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={`Запчасти к ТО: ${item.ruleName}`}
      description="Позиции уйдут заявкой в закупки проекта; количество и единицы поправьте в самой заявке."
      width="sm"
      footer={
        <>
          <Button onClick={onClose}>Отмена</Button>
          <Button
            variant="primary"
            disabled={!lines.length}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Создать заявку
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {save.isError ? <ErrorBox error={save.error} /> : null}
        <LField label="Позиции — по строке на каждую">
          <Textarea rows={5} value={body} onChange={(e) => setText(e.target.value)} />
        </LField>
        <LField label="Нужно к дате">
          <Input
            type="date"
            className="h-11"
            value={required}
            onChange={(e) => setRequired(e.target.value)}
          />
        </LField>
      </div>
    </Dialog>
  );
}

function RulesDialog({ canEdit, onClose }: { canEdit: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['fleet', 'service-rules'],
    queryFn: () => api<ServiceRuleRow[]>('/fleet/service-rules'),
  });
  const empty = { id: '', name: '', model: '', hours: '', days: '', parts: '' };
  const [f, setF] = useState(empty);
  const refresh = () => qc.invalidateQueries({ queryKey: ['fleet'] });
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: f.name.trim(),
        model: f.model.trim() || null,
        everyHours: num(f.hours),
        everyDays: num(f.days),
        partsHint: f.parts.trim() || null,
      };
      return f.id
        ? api(`/fleet/service-rules/${f.id}`, { method: 'PATCH', body })
        : api('/fleet/service-rules', { method: 'POST', body });
    },
    onSuccess: () => {
      setF(empty);
      void refresh();
    },
  });
  const del = useMutation({
    mutationFn: (id: string) => api(`/fleet/service-rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => void refresh(),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title="Регламент ТО"
      description="Через сколько моточасов или дней нужно ТО. Модель пустая — правило для всей техники компании."
      width="lg"
      footer={<Button onClick={onClose}>Закрыть</Button>}
    >
      {q.isPending ? <Spinner /> : null}
      <ul className="divide-y divide-line">
        {(q.data ?? []).map((r) => (
          <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[14px]">
            <span>
              {r.name}
              <span className="block text-[12px] text-ink-2">
                {r.model ? `модель ${r.model}` : 'вся техника'} ·{' '}
                {[
                  r.everyHours != null ? `${n1(r.everyHours)} мч` : null,
                  r.everyDays != null ? `${r.everyDays} дн.` : null,
                ]
                  .filter(Boolean)
                  .join(' или ')}{' '}
                · единиц: {r.units}
              </span>
            </span>
            {canEdit ? (
              <span className="flex gap-1">
                <Button
                  onClick={() =>
                    setF({
                      id: r.id,
                      name: r.name,
                      model: r.model ?? '',
                      hours: r.everyHours == null ? '' : String(r.everyHours),
                      days: r.everyDays == null ? '' : String(r.everyDays),
                      parts: r.partsHint ?? '',
                    })
                  }
                >
                  Изменить
                </Button>
                <Button
                  onClick={() => {
                    if (
                      window.confirm(
                        `Удалить регламент «${r.name}»? Отметки ТО по нему тоже удалятся, прогноз по нему пропадёт.`,
                      )
                    )
                      del.mutate(r.id);
                  }}
                >
                  Удалить
                </Button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {canEdit ? (
        <div className="mt-3 flex flex-col gap-3 rounded-md border border-line p-3">
          {save.isError ? <ErrorBox error={save.error} /> : null}
          <h3 className="text-[13px] font-semibold">{f.id ? 'Изменить регламент' : 'Новый регламент'}</h3>
          <LField label="Название (ТО-250, сезонное…)">
            <Input className="h-11" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          </LField>
          <LField label="Модель (пусто — для всей техники)">
            <Input className="h-11" value={f.model} onChange={(e) => setF({ ...f, model: e.target.value })} />
          </LField>
          <div className="grid grid-cols-2 gap-3">
            <LField label="Каждые, моточасов">
              <Input
                inputMode="decimal"
                className="h-11"
                value={f.hours}
                onChange={(e) => setF({ ...f, hours: e.target.value })}
              />
            </LField>
            <LField label="или каждые, дней">
              <Input
                inputMode="numeric"
                className="h-11"
                value={f.days}
                onChange={(e) => setF({ ...f, days: e.target.value })}
              />
            </LField>
          </div>
          <LField label="Что обычно нужно (по строке на позицию)">
            <Textarea rows={3} value={f.parts} onChange={(e) => setF({ ...f, parts: e.target.value })} />
          </LField>
          <div className="flex gap-2">
            <Button
              variant="primary"
              disabled={!f.name.trim() || (!num(f.hours) && !num(f.days))}
              loading={save.isPending}
              onClick={() => save.mutate()}
            >
              {f.id ? 'Сохранить' : 'Добавить'}
            </Button>
            {f.id ? <Button onClick={() => setF(empty)}>Отмена</Button> : null}
          </div>
        </div>
      ) : (
        <p className="mt-3 text-[13px] text-ink-2">
          Регламент ведёт тот, у кого есть право «Ресурсы: правка» на всю компанию.
        </p>
      )}
    </Dialog>
  );
}
